/**
 * useTranscriptScroll — the shared pinned-to-bottom scroll controller for
 * every chat transcript surface (GlobalChatSystem, the landing-page demo
 * chat, the Hidalgo widget, Moonshot voice chat).
 *
 * Model (2026-08 scroll redesign):
 * - `pinned` (the single source of truth, a ref — no stale snapshots): while
 *   pinned, the viewport tracks the bottom through ANY content growth — new
 *   messages, streaming edits, follow-up chips resolving after the reply,
 *   image loads, post-paint decorations — via one layout effect on the items
 *   plus a ResizeObserver on the content wrapper and the scroller box.
 * - Upward user intent (wheel up, touch drag, ArrowUp/PageUp/Home/Shift+Space)
 *   or any non-programmatic scroll that leaves the small bottom band unpins
 *   IMMEDIATELY. While unpinned NOTHING auto-scrolls; genuinely-new tail
 *   messages light the "N new" pill instead.
 * - Scrolling back into the band, clicking the pill, or sending re-pins.
 * - Per-context (thread) reading positions are remembered IN MEMORY only and
 *   restored on switch while fresh; a page reload always opens at the bottom.
 *   There is deliberately NO localStorage scroll persistence — the old global
 *   blob was shared across surfaces and threads and caused cross-clobbering.
 *
 * Corrective writes are instant `scrollTop` assignments: transcript
 * containers must NOT set CSS `scroll-behavior: smooth` (see scrollMotion.ts).
 * Smoothness is only ever explicit, and honors prefers-reduced-motion.
 */

import { useCallback, useLayoutEffect, useEffect, useRef, useState } from 'react';
import {
  BOTTOM_PIN_BAND_PX,
  NEAR_TOP_THRESHOLD_PX,
  POST_SWITCH_SETTLE_MS,
  TOUCH_UNPIN_SLOP_PX,
  PROGRAMMATIC_ARRIVAL_EPSILON_PX,
  PROGRAMMATIC_SCROLL_TIMEOUT_MS,
  distanceFromBottom,
  isWithinBottomBand,
  isNearTranscriptTop,
  shapeOfItems,
  classifyItemsChange,
  recordThreadScroll,
  lookupFreshThreadScroll,
  ThreadScrollMemory,
  TranscriptItemsShape,
} from '../utils/transcriptScrollPolicy';
import { resolveScrollBehavior, smartBottomBehavior, prefersReducedMotion } from '../utils/scrollMotion';
import {
  pickPreviousMessageScrollTop,
  pickNextMessageScrollTop,
  isAtThreadTopEdge,
  isAtThreadBottomEdge,
  anchorContentTop,
  landingIndex,
} from '../components/GlobalChatSystem/threadNav';

export interface UseTranscriptScrollArgs {
  /** The overflow-y:auto transcript scroller. */
  containerRef: React.RefObject<HTMLDivElement>;
  /**
   * Inner wrapper around ALL transcript content (messages, chips, dividers).
   * The ResizeObserver watches it so post-paint growth that changes nothing
   * in `items` — follow-up chips resolving, images decoding, copy-button
   * decoration — still re-sticks a pinned viewport.
   */
  contentRef: React.RefObject<HTMLDivElement>;
  /** The rendered transcript items, in order. Only `id` is read. */
  items: ReadonlyArray<{ id?: string | null }>;
  /**
   * Synchronous accessor for the current context (thread) key. Must never lag
   * a thread transition — GlobalChatSystem feeds the thread store's sync
   * accessor through its bridge ref. Single-conversation surfaces pass a
   * constant. `null` disables per-context memory (position still works).
   */
  getContextKey: () => string | null;
  /** Whether the surface is visible. Reopening while pinned re-sticks. */
  isOpen: boolean;
}

/** 1-based position of the last stepper landing, for an orientation readout. */
export interface TranscriptStepPosition {
  index: number;
  total: number;
}

export interface TranscriptScrollApi {
  /** Show the "jump to latest" pill (viewport is away from the bottom). */
  showScrollToBottom: boolean;
  /**
   * Synchronous read of the live pinned state (the ref, not the lagging pill
   * state). The persisted view-marker writer in GlobalChatSystem keys its
   * clear-vs-save decision on this — pinned-to-bottom must CLEAR the marker
   * so bottom stays the default, and the pill state can lag a band re-entry
   * by a commit.
   */
  isPinnedNow: () => boolean;
  /** Viewport is near the transcript top (shows the "previous X" pill). */
  isNearTop: boolean;
  /** Count of tail messages that arrived while unpinned. */
  newMessageCount: number;
  /** In-place content updates happened while unpinned. */
  hasUnseenActivity: boolean;
  /** Strict edge: parked at the absolute top of the transcript. */
  isAtThreadTop: boolean;
  /** Strict edge: parked at the absolute bottom of the transcript. */
  isAtThreadBottom: boolean;
  /** Jump to the latest message and re-pin (pill click / send paths). */
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  /** Alias of scrollToBottom for send paths — sending ALWAYS re-pins. */
  pinToBottom: (behavior?: ScrollBehavior) => void;
  /** Momentum-style animated scroll to the top (reveal-older flow). Unpins. */
  scrollToTopAnimated: (onComplete?: () => void) => void;
  /**
   * Where the last ↑/↓ step landed (1-based), or null before any stepping.
   * Updated only by stepping, so it is an orientation aid for that gesture —
   * not a live scroll position.
   */
  stepPosition: TranscriptStepPosition | null;
  /** Step the viewport up to the previous message bubble. Unpins. */
  stepToPreviousMessage: () => void;
  /** Step the viewport down to the next message bubble. */
  stepToNextMessage: () => void;
}

interface ProgrammaticScrollState {
  active: boolean;
  targetTop: number;
  timer: number | null;
}

/** PROD-185: max synchronous re-entries of the items layout effect within one
 *  macrotask window before the breaker skips the body. Normal flow commits
 *  the effect a handful of times per flush; only a genuine update loop
 *  approaches this. Kept far below React's 50-nested-update ceiling so the
 *  crash line is unreachable through this hook. */
export const ITEMS_EFFECT_MAX_SYNC_RUNS = 12;

export function useTranscriptScroll({
  containerRef,
  contentRef,
  items,
  getContextKey,
  isOpen,
}: UseTranscriptScrollArgs): TranscriptScrollApi {
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isNearTop, setIsNearTop] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [hasUnseenActivity, setHasUnseenActivity] = useState(false);
  const [isAtThreadTop, setIsAtThreadTop] = useState(true);
  const [isAtThreadBottom, setIsAtThreadBottom] = useState(true);
  // Where the stepper last landed, for the "12 / 40" readout. Written ONLY
  // from stepToMessage — a deliberate user gesture, a handful per second at
  // most — never from the scroll listener. A scroll-rate index recompute
  // would mean a setState per scroll event on the crash-sensitive path
  // PROD-185 came from; orientation while stepping is not worth that.
  const [stepPosition, setStepPosition] = useState<TranscriptStepPosition | null>(null);

  // Pinned-to-bottom is the single source of truth for auto-scroll decisions.
  const pinnedRef = useRef(true);
  // Tracks controller-driven SMOOTH scrolls so the band machine doesn't
  // misread them as user intent while they glide through the unpin zone.
  const programmaticRef = useRef<ProgrammaticScrollState>({ active: false, targetTop: 0, timer: null });
  const touchStartYRef = useRef<number | null>(null);
  // `undefined` = never ran (first mount → bottom); `null` = keyless context.
  const lastContextKeyRef = useRef<string | null | undefined>(undefined);
  // ── Items-effect re-entry breaker (PROD-185, 2026-08-19) ──
  // Production crash (sourcemap-decoded from the deployed bundle): Minified
  // React #185 — maximum update depth — thrown from THIS hook's
  // setIsAtThreadTop during the items layout effect, on a Regenerate click.
  // The effect keys on `items` identity; when an upstream actor churns the
  // message-array identity every commit (a non-converging reconcile /
  // dispatch cycle), this layout effect re-runs NESTED on every commit of
  // the cascade and its edge-state setters become the updates that trip
  // React's 50-nested-update ceiling — taking the whole surface down with
  // the red error screen instead of, at worst, a stale scroll pill.
  // The breaker counts synchronous re-entries; past the ceiling it SKIPS the
  // effect body (no setStates, no scroll writes) so this hook can never be
  // the fuel that crosses React's limit. A macrotask resets the counter —
  // normal cadence (a handful of commits per flush) never comes close.
  const itemsEffectBurstRef = useRef(0);
  const itemsEffectBreakerTrippedRef = useRef(false);
  const lastSwitchAtRef = useRef(0);
  const prevShapeRef = useRef<TranscriptItemsShape>({ firstId: null, lastId: null, count: 0 });
  const memoryRef = useRef<ThreadScrollMemory>(new Map());
  // Clamp-echo guard (returning-tab reliability, 2026-09-01): when the
  // switch-branch restore has to CLAMP a remembered scrollTop (the transcript
  // isn't fully laid out yet — the collapsed last-10 render keeps
  // scrollHeight small), the clamped assignment fires an async scroll event
  // that handleScroll would re-record, overwriting the accurate remembered
  // position with the clamped one. This flag skips exactly that ONE echo
  // record; the next genuine user scroll records normally.
  const skipNextRecordRef = useRef(false);
  // Kept in a ref so the stable event handlers always read the live accessor.
  const getContextKeyRef = useRef(getContextKey);
  getContextKeyRef.current = getContextKey;

  const endProgrammatic = useCallback(() => {
    const p = programmaticRef.current;
    if (p.timer !== null) {
      window.clearTimeout(p.timer);
      p.timer = null;
    }
    p.active = false;
  }, []);

  const beginProgrammatic = useCallback((targetTop: number) => {
    const p = programmaticRef.current;
    if (p.timer !== null) window.clearTimeout(p.timer);
    p.active = true;
    p.targetTop = targetTop;
    // Safety valve: interrupted/retargeted animations must not leave the
    // band machine disabled.
    p.timer = window.setTimeout(endProgrammatic, PROGRAMMATIC_SCROLL_TIMEOUT_MS);
  }, [endProgrammatic]);

  // Mirrors of the three edge flags. Every write below is VALUE-GUARDED
  // against these: when the geometry hasn't actually crossed an edge — the
  // overwhelmingly common case, since this runs on every scroll event, every
  // ResizeObserver callback and every items commit — the hook enqueues NO
  // state update at all.
  //
  // This is a PROD-185 invariant, not a micro-optimisation. That crash was
  // React #185 (maximum update depth) raised from setIsAtThreadTop inside the
  // items layout effect while an upstream actor churned the message array
  // every commit: unconditional setters made this hook a willing participant
  // in the cascade. Guarded, it can only contribute an update when a real
  // edge transition happened, so it cannot supply per-commit fuel even if
  // some future upstream starts churning again. The re-entry breaker below
  // stays as the second, independent line of defence.
  const isAtThreadTopRef = useRef(true);
  const isAtThreadBottomRef = useRef(true);
  const isNearTopRef = useRef(false);
  // Same guard for the jump-to-latest pill: the scroll handler decides its
  // visibility on EVERY scroll event, so an unguarded setter re-rendered the
  // whole chat surface for a value that had not changed.
  const showScrollToBottomRef = useRef(false);
  const setPillVisible = useCallback((next: boolean) => {
    if (next === showScrollToBottomRef.current) return;
    showScrollToBottomRef.current = next;
    setShowScrollToBottom(next);
  }, []);

  const refreshEdgeStates = useCallback((el: HTMLDivElement) => {
    const nextAtTop = isAtThreadTopEdge(el.scrollTop);
    if (nextAtTop !== isAtThreadTopRef.current) {
      isAtThreadTopRef.current = nextAtTop;
      setIsAtThreadTop(nextAtTop);
    }
    const nextAtBottom = isAtThreadBottomEdge(el.scrollTop, el.scrollHeight, el.clientHeight);
    if (nextAtBottom !== isAtThreadBottomRef.current) {
      isAtThreadBottomRef.current = nextAtBottom;
      setIsAtThreadBottom(nextAtBottom);
    }
    const nextNearTop = isNearTranscriptTop(el.scrollTop);
    if (nextNearTop !== isNearTopRef.current) {
      isNearTopRef.current = nextNearTop;
      setIsNearTop(nextNearTop);
    }
  }, []);

  const stickToBottomInstant = useCallback((el: HTMLDivElement) => {
    el.scrollTop = el.scrollHeight;
    refreshEdgeStates(el);
  }, [refreshEdgeStates]);

  const pinToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = containerRef.current;
    if (!el) return;
    pinnedRef.current = true;
    setPillVisible(false);
    setNewMessageCount(0);
    setHasUnseenActivity(false);
    const target = Math.max(0, el.scrollHeight - el.clientHeight);
    const dist = distanceFromBottom(el.scrollTop, el.scrollHeight, el.clientHeight);
    const resolved = behavior === 'smooth'
      ? smartBottomBehavior(dist, el.clientHeight)
      : resolveScrollBehavior(behavior);
    if (resolved === 'smooth' && typeof el.scrollTo === 'function') {
      beginProgrammatic(target);
      el.scrollTo({ top: target, behavior: 'smooth' });
    } else {
      endProgrammatic();
      el.scrollTop = el.scrollHeight;
    }
    refreshEdgeStates(el);
  }, [containerRef, beginProgrammatic, endProgrammatic, refreshEdgeStates, setPillVisible]);

  const scrollToBottom = pinToBottom;

  /**
   * Fast momentum-based scroll to the top (the reveal-older landing).
   * Unpins FIRST so a mid-animation append can never yank the view back
   * down. easeOutExpo, 300–600ms scaled by distance; instant under
   * prefers-reduced-motion.
   */
  const scrollToTopAnimated = useCallback((onComplete?: () => void) => {
    const el = containerRef.current;
    if (!el) {
      onComplete?.();
      return;
    }
    pinnedRef.current = false;
    setPillVisible(true);
    endProgrammatic();

    const startScrollTop = el.scrollTop;
    const distance = startScrollTop;
    if (distance <= 0 || prefersReducedMotion()) {
      el.scrollTop = 0;
      // refreshEdgeStates derives near-top from the DOM and keeps the guard
      // refs in sync — writing the near-top flag raw here would desync them.
      refreshEdgeStates(el);
      onComplete?.();
      return;
    }

    const baseDuration = 300;
    const maxDuration = 600;
    const distanceFactor = Math.min(distance / 2000, 1);
    const duration = baseDuration + (maxDuration - baseDuration) * distanceFactor;
    const startTime = performance.now();
    const easeOutExpo = (t: number): number => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

    const animateScroll = (currentTime: number) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      el.scrollTop = startScrollTop - distance * easeOutExpo(progress);
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        el.scrollTop = 0;
        refreshEdgeStates(el);
        onComplete?.();
      }
    };
    // First frame synchronous — no ~16ms rAF wait before movement starts.
    animateScroll(performance.now());
  }, [containerRef, endProgrammatic, refreshEdgeStates, setPillVisible]);

  // Message-step navigation: walk the transcript one bubble at a time over
  // the [data-msg-nav] anchors the surface stamps on message containers;
  // targets come from the pure pickers in threadNav.ts. Stepping below the
  // last bubble hands off to scrollToBottom so ↓ converges on "latest".
  const stepToMessage = useCallback((direction: 'previous' | 'next') => {
    const el = containerRef.current;
    if (!el) return;
    // Zero-height anchors are messages inside a collapsed reveal-older
    // container — invisible, and their rects all degenerate to the same
    // spot. Stepping only walks what the user can actually see.
    const anchors = Array.from(el.querySelectorAll<HTMLElement>('[data-msg-nav]'))
      .filter(anchor => anchor.getBoundingClientRect().height > 0);
    if (anchors.length === 0) return;

    const containerRectTop = el.getBoundingClientRect().top;
    const anchorTops = anchors.map(anchor =>
      anchorContentTop(anchor.getBoundingClientRect().top, containerRectTop, el.scrollTop)
    );

    if (direction === 'previous') {
      // Stepping up is upward intent — unpin before the glide.
      pinnedRef.current = false;
      setPillVisible(true);
      const target = pickPreviousMessageScrollTop(anchorTops, el.scrollTop);
      setStepPosition({ index: landingIndex(anchorTops, target), total: anchorTops.length });
      if (typeof el.scrollTo === 'function') {
        el.scrollTo({ top: target, behavior: resolveScrollBehavior('smooth') });
      } else {
        el.scrollTop = target;
      }
    } else {
      const target = pickNextMessageScrollTop(anchorTops, el.scrollTop);
      if (target === null) {
        // Past the last bubble the step converges on "latest" — and so does
        // the readout.
        setStepPosition({ index: anchorTops.length, total: anchorTops.length });
        scrollToBottom('smooth');
        return;
      }
      setStepPosition({ index: landingIndex(anchorTops, target), total: anchorTops.length });
      // No programmatic marker here: if the step lands inside the bottom
      // band the band machine re-pins, which is the intended convergence.
      if (typeof el.scrollTo === 'function') {
        el.scrollTo({ top: target, behavior: resolveScrollBehavior('smooth') });
      } else {
        el.scrollTop = target;
      }
    }
  }, [containerRef, scrollToBottom]);

  const stepToPreviousMessage = useCallback(() => stepToMessage('previous'), [stepToMessage]);
  const stepToNextMessage = useCallback(() => stepToMessage('next'), [stepToMessage]);

  // Live pinned state for the persisted view-marker writer (interface
  // docblock). Stable identity; reads the ref, enqueues nothing.
  const isPinnedNow = useCallback(() => pinnedRef.current, []);

  // ──────────────────────────────────────────────────────────────────────
  // Scroll listener: band machine + per-context memory recording.
  // ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;

    const handleScroll = () => {
      // Guarded helper, NOT three raw setters: this runs on every scroll event,
      // and only a real edge transition may enqueue an update (see the
      // value-guard docblock above).
      refreshEdgeStates(el);

      const p = programmaticRef.current;
      if (p.active) {
        // Controller-driven smooth scroll in flight: only watch for arrival;
        // never let the glide's intermediate positions unpin or record.
        if (Math.abs(el.scrollTop - p.targetTop) <= PROGRAMMATIC_ARRIVAL_EPSILON_PX) {
          endProgrammatic();
        }
        return;
      }

      const inBand = isWithinBottomBand(el.scrollTop, el.scrollHeight, el.clientHeight, BOTTOM_PIN_BAND_PX);
      if (inBand) {
        pinnedRef.current = true;
        setPillVisible(false);
        setNewMessageCount(0);
        setHasUnseenActivity(false);
      } else {
        pinnedRef.current = false;
        setPillVisible(true);
      }

      const key = getContextKeyRef.current ? getContextKeyRef.current() : null;
      if (key) {
        if (skipNextRecordRef.current) {
          // The one echo event of a clamped switch-branch restore — never
          // record it (see the ref docblock above).
          skipNextRecordRef.current = false;
        } else {
          recordThreadScroll(memoryRef.current, key, {
            scrollTop: el.scrollTop,
            pinned: pinnedRef.current,
            savedAt: Date.now(),
          });
        }
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isOpen, containerRef, endProgrammatic, refreshEdgeStates, setPillVisible]);

  // ──────────────────────────────────────────────────────────────────────
  // Upward user intent unpins immediately — BEFORE the resulting scroll
  // event — and cancels any in-flight programmatic glide so the user's
  // gesture always wins over the controller.
  // ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;

    const unpinFromUserIntent = () => {
      if (programmaticRef.current.active) {
        endProgrammatic();
        // Halt the browser's smooth glide by re-targeting the current spot.
        if (typeof el.scrollTo === 'function') {
          el.scrollTo({ top: el.scrollTop, behavior: 'auto' });
        }
      }
      pinnedRef.current = false;
      setPillVisible(true);
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) unpinFromUserIntent();
    };
    const handleTouchStart = (e: TouchEvent) => {
      touchStartYRef.current = e.touches.length ? e.touches[0].clientY : null;
      // Any touch during a programmatic glide reclaims control for the user.
      if (programmaticRef.current.active) unpinFromUserIntent();
    };
    const handleTouchMove = (e: TouchEvent) => {
      const startY = touchStartYRef.current;
      if (startY === null || !e.touches.length) return;
      // Finger travels DOWN the screen = content scrolls up = reading back.
      if (e.touches[0].clientY - startY > TOUCH_UNPIN_SLOP_PX) {
        unpinFromUserIntent();
        touchStartYRef.current = null;
      }
    };
    const handleTouchEnd = () => {
      touchStartYRef.current = null;
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      const upKeys = ['ArrowUp', 'PageUp', 'Home'];
      if (upKeys.includes(e.key) || (e.key === ' ' && e.shiftKey)) {
        unpinFromUserIntent();
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: true });
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    el.addEventListener('keydown', handleKeyDown);
    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      el.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, containerRef, endProgrammatic, refreshEdgeStates, setPillVisible]);

  // ──────────────────────────────────────────────────────────────────────
  // The unified items effect: context switches and message-list changes.
  // Layout effect so switch landings and pinned sticks commit BEFORE paint —
  // the user never sees the previous thread's offset flash on the new one.
  // ──────────────────────────────────────────────────────────────────────
  useLayoutEffect(() => {
    // PROD-185 re-entry breaker (see the ref docblock above). The counter
    // resets on the next macrotask; a synchronous commit cascade that
    // re-enters this effect past the ceiling gets NO further state updates
    // or scroll writes from this hook.
    itemsEffectBurstRef.current += 1;
    if (itemsEffectBurstRef.current === 1) {
      window.setTimeout(() => {
        itemsEffectBurstRef.current = 0;
        itemsEffectBreakerTrippedRef.current = false;
      }, 0);
    }
    if (itemsEffectBurstRef.current > ITEMS_EFFECT_MAX_SYNC_RUNS) {
      if (!itemsEffectBreakerTrippedRef.current) {
        itemsEffectBreakerTrippedRef.current = true;
        console.warn(
          '[useTranscriptScroll] items-effect re-entry breaker tripped — an upstream update loop is churning the message list; skipping scroll/edge refresh for this burst',
        );
      }
      return;
    }
    const nextShape = shapeOfItems(items);
    const el = containerRef.current;
    const key = getContextKeyRef.current ? getContextKeyRef.current() : null;

    if (key !== lastContextKeyRef.current) {
      // Context (thread) switch — or the very first run. Never a badge:
      // switching threads is navigation, not new activity.
      lastContextKeyRef.current = key;
      lastSwitchAtRef.current = Date.now();
      prevShapeRef.current = nextShape;
      setNewMessageCount(0);
      setHasUnseenActivity(false);
      if (!el) return;
      const entry = key ? lookupFreshThreadScroll(memoryRef.current, key, Date.now()) : null;
      if (entry && !entry.pinned) {
        // Fresh unpinned memory: put the reader back where they left off.
        const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
        const clamped = Math.min(Math.max(entry.scrollTop, 0), maxTop);
        // Arm the clamp-echo guard ONLY when the write both clamps AND will
        // actually move the scroller (an unmoved scrollTop fires no event, so
        // arming would swallow the next genuine scroll's record instead).
        if (clamped !== entry.scrollTop && clamped !== el.scrollTop) {
          skipNextRecordRef.current = true;
        }
        el.scrollTop = clamped;
        pinnedRef.current = false;
        setPillVisible(true);
      } else {
        // Default landing: bottom, pinned — latest message and follow-up
        // chips fully visible.
        el.scrollTop = el.scrollHeight;
        pinnedRef.current = true;
        setPillVisible(false);
      }
      refreshEdgeStates(el);
      return;
    }

    const kind = classifyItemsChange(prevShapeRef.current, nextShape);
    const tailDelta = Math.max(0, nextShape.count - prevShapeRef.current.count);
    prevShapeRef.current = nextShape;
    if (!el || kind === 'none') return;

    if (kind === 'prepend') {
      // Load-earlier page: the surface's own anchor effect owns the scroll
      // correction. Stay inert — and never badge history as "new".
      refreshEdgeStates(el);
      return;
    }

    if (pinnedRef.current) {
      stickToBottomInstant(el);
      return;
    }

    // Unpinned: nothing moves. Server hydration right after a switch is
    // absorbed silently; real activity lights the pills.
    const withinSettle = Date.now() - lastSwitchAtRef.current < POST_SWITCH_SETTLE_MS;
    if (!withinSettle) {
      if (kind === 'append') {
        setNewMessageCount(prev => prev + tailDelta);
      } else {
        setHasUnseenActivity(true);
      }
    }
    refreshEdgeStates(el);
  }, [items]);

  // ──────────────────────────────────────────────────────────────────────
  // Growth tracking beyond `items`: follow-up chips resolving, images
  // decoding, copy-button decoration, keyboard/mode resizes. jsdom has no
  // ResizeObserver — the items effect above is the test-environment path.
  // ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return undefined;
    if (typeof ResizeObserver === 'undefined') return undefined;
    const el = containerRef.current;
    if (!el) return undefined;

    const observer = new ResizeObserver(() => {
      const node = containerRef.current;
      if (!node) return;
      if (pinnedRef.current) {
        node.scrollTop = node.scrollHeight;
      }
      refreshEdgeStates(node);
    });
    observer.observe(el);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [isOpen, containerRef, contentRef, refreshEdgeStates]);

  // Reopening the surface: heights may have changed while hidden. Pinned →
  // re-stick; unpinned → leave the preserved reading position untouched.
  useEffect(() => {
    if (!isOpen) return;
    const el = containerRef.current;
    if (!el) return;
    if (pinnedRef.current) {
      el.scrollTop = el.scrollHeight;
      setPillVisible(false);
    }
    refreshEdgeStates(el);
  }, [isOpen, containerRef, refreshEdgeStates, setPillVisible]);

  return {
    showScrollToBottom,
    isPinnedNow,
    isNearTop,
    newMessageCount,
    hasUnseenActivity,
    isAtThreadTop,
    isAtThreadBottom,
    scrollToBottom,
    pinToBottom,
    scrollToTopAnimated,
    stepPosition,
    stepToPreviousMessage,
    stepToNextMessage,
  };
}
